import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import { useT } from '../lib/i18n';
import { useDB } from '../lib/useDB';
import { useAuth } from '../hooks/useAuth';

export function SessionsTable() {
  const sessions = useSelector((s: RootState) => s.sessions.list);
  const { user } = useAuth();
  const { removeSession } = useDB(user);
  const { t }     = useT();
  const [filterDate, setFilterDate]         = useState('');
  const [filterPlatform, setFilterPlatform] = useState('');
  const [filterType, setFilterType]         = useState('');
  const [confirmDel, setConfirmDel]         = useState<string | null>(null);
  const [expanded, setExpanded]             = useState<string | null>(null);

  const filtered = sessions.filter(s => {
    if (filterDate && !s.date.startsWith(filterDate)) return false;
    if (filterPlatform && s.platform !== filterPlatform) return false;
    if (filterType && s.type !== filterType) return false;
    return true;
  });

  const platforms = [...new Set(sessions.map(s => s.platform))];

  const handleDelete = (id: string) => {
    if (confirmDel === id) { removeSession(id); setConfirmDel(null); }
    else { setConfirmDel(id); setTimeout(() => setConfirmDel(null), 3000); }
  };

  const exportCSV = () => {
    const header = `${t('sessionsTable.date')},${t('sessionsTable.type')},${t('sessionsTable.platform')},${t('session.buyIn')},${t('session.cashOut')},${t('session.grossProfit')},${t('session.grossProfit')} Net,${t('staking.title')},Backer`;
    const rows = [header, ...sessions.map(s => `${s.date},${s.type},${s.platform},${s.totalInvested},${s.totalReturned},${s.grossProfit},${s.netProfit},${s.staking.isStaked},${s.staking.stakerName}`)].join('\n');
    const blob = new Blob([rows], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url;
    a.download = `poker-sessions-${new Date().toISOString().split('T')[0]}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  return (
    <div className="sessions-view">
      <div className="sv-header">
        <h2 className="form-title">{t('sessionsTable.title')} ({filtered.length})</h2>
        <div className="sv-controls">
          <input type="month" value={filterDate} onChange={e => setFilterDate(e.target.value)} className="filter-input" />
          <select value={filterPlatform} onChange={e => setFilterPlatform(e.target.value)} className="filter-input">
            <option value="">{t('sessionsTable.platform')}</option>
            {platforms.map(p => <option key={p}>{p}</option>)}
          </select>
          <select value={filterType} onChange={e => setFilterType(e.target.value)} className="filter-input">
            <option value="">{t('sessionsTable.type')}</option>
            <option value="cash">{t('sessionsTable.cash')}</option>
            <option value="tournament">{t('sessionsTable.tournament')}</option>
            <option value="multi">{t('sessionsTable.multi')}</option>
          </select>
          {(filterDate || filterPlatform || filterType) &&
            <button className="btn-clear-filter" onClick={() => { setFilterDate(''); setFilterPlatform(''); setFilterType(''); }}>✕</button>}
          <button className="btn btn-secondary" onClick={exportCSV}>↓ {t('sessionsTable.export')}</button>
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="empty-state small"><p>{t('sessionsTable.noResults')}</p></div>
      ) : (
        <div className="sessions-list">
          {filtered.map(s => (
            <div key={s.id} className={`session-row ${s.netProfit >= 0 ? 'win' : 'loss'}`}>
              <div className="sr-main" onClick={() => setExpanded(expanded === s.id ? null : s.id)}>
                <span className="sr-date">{s.date}</span>
                <span className="sr-badge">{s.type === 'cash' ? t('sessionsTable.cash').toUpperCase() : s.type === 'multi' ? `${s.tournaments.length}T` : s.tournaments[0]?.format || 'MTT'}</span>
                <span className="sr-platform">{s.platform}</span>
                {s.type === 'cash' && s.stakes && <span className="sr-stakes">{s.stakes}</span>}
                <span className="sr-inv">${s.totalInvested.toFixed(2)}</span>
                <span className={`sr-profit ${s.netProfit >= 0 ? 'pos' : 'neg'}`}>{s.netProfit >= 0 ? '+' : ''}${s.netProfit.toFixed(2)}</span>
                {s.staking.isStaked && <span className="sr-staked" title={`${s.staking.myPercent}% - ${s.staking.stakerName}`}>S</span>}
                <span className="sr-expand">{expanded === s.id ? '▲' : '▼'}</span>
              </div>

              {expanded === s.id && (
                <div className="sr-detail">
                  {s.type !== 'cash' && s.tournaments.map((tr, i) => (
                    <div key={tr.id} className="sr-tourney">
                      <span>#{i+1} {tr.name || tr.format}</span>
                      <span>BI: ${tr.buyIn}{tr.rebuys > 0 ? ` +${tr.rebuys}R` : ''}{tr.addon > 0 ? ` +A` : ''}</span>
                      <span>Prize: ${tr.prize}{tr.bountiesWon > 0 ? ` +$${tr.bountiesWon}b` : ''}</span>
                      {tr.finishPosition && <span>#{tr.finishPosition}{tr.totalEntrants ? `/${tr.totalEntrants}` : ''}</span>}
                      <span className={(tr.prize + tr.bountiesWon - tr.buyIn - tr.rebuys * tr.buyIn - tr.addon) >= 0 ? 'pos' : 'neg'}>
                        {(tr.prize + tr.bountiesWon - tr.buyIn - tr.rebuys * tr.buyIn - tr.addon) >= 0 ? '+' : ''}
                        ${(tr.prize + tr.bountiesWon - tr.buyIn - tr.rebuys * tr.buyIn - tr.addon).toFixed(2)}
                      </span>
                    </div>
                  ))}
                  {s.notes && <div className="sr-notes">📝 {s.notes}</div>}
                  {s.staking.isStaked && (
                    <div className="sr-staking-info">
                      {t('staking.title')}: {s.staking.stakerName} — {t('staking.gross')}: {s.grossProfit >= 0 ? '+' : ''}${s.grossProfit.toFixed(2)} → {t('staking.myPart')} {s.staking.myPercent}%: {s.netProfit >= 0 ? '+' : ''}${s.netProfit.toFixed(2)}
                    </div>
                  )}
                  <button className={`btn-delete-session ${confirmDel === s.id ? 'confirming' : ''}`} onClick={() => handleDelete(s.id)}>
                    {confirmDel === s.id ? `⚠ ${t('sessionsTable.deleteConfirm')}` : `✕ ${t('common.delete')}`}
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
